import { ArrowUpRight } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";
import { scrollToSection } from "@/lib/lenis";

const PRODUCTS = [
  {
    id: "cow-milk",
    name: "Cow Milk",
    gujarati: "તાજું ગાયનું દૂધ",
    badge: "Daily Essential",
    description:
      "Pure organic cow milk procured fresh every day from village farmers and our own shed — processed with no synthetic additives, hormones or chemicals.",
    image:
      "https://images.unsplash.com/photo-1639151082235-406d8eb262b9?auto=format&fit=crop&w=900&q=80",
  },
  {
    id: "curd",
    name: "Curd",
    gujarati: "શુદ્ધ દહીં",
    badge: "Probiotic Rich",
    description:
      "Thick, naturally set curd made from our own milk and fermented slowly — the way Gujarati kitchens have always made it.",
    image:
      "https://images.unsplash.com/photo-1733197012835-e99ebf3c914d?auto=format&fit=crop&w=900&q=80",
  },
  {
    id: "ghee",
    name: "Ghee",
    gujarati: "શુદ્ધ ઘી",
    badge: "Gold Standard",
    description:
      "Golden ghee crafted from fresh curd at our local facility — rich in aroma and flavour, made the traditional way.",
    image:
      "https://images.unsplash.com/photo-1568657704598-602700bd9694?auto=format&fit=crop&w=900&q=80",
  },
  {
    id: "buttermilk",
    name: "Buttermilk",
    gujarati: "તાજી છાશ",
    badge: "Digestive Tonic",
    description:
      "Cool, refreshing chaas churned from our curd — the Ayurvedic after-meal companion for good digestion.",
    image:
      "https://images.unsplash.com/photo-1555791902-2bd22de5cd9c?auto=format&fit=crop&w=900&q=80",
  },
];

export default function Products() {
  const enquire = (name) => {
    window.dispatchEvent(new CustomEvent("adveshta:interest", { detail: name }));
    scrollToSection("contact");
  };

  return (
    <section id="products" data-testid="products-section" className="bg-cream py-24 lg:py-36">
      <div className="max-w-[1440px] mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <Reveal>
            <Eyebrow tone="brand">06 — From Our Dairy</Eyebrow>
            <h2 className="mt-6 font-serif font-semibold text-4xl md:text-5xl leading-[1.05] tracking-tight text-pine">
              Four ways we pour purity.
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-sage md:text-right md:max-w-xs">
              Home delivery in & around Vyara city
            </p>
          </Reveal>
        </div>

        <div className="mt-14 grid sm:grid-cols-2 xl:grid-cols-4 gap-5 md:gap-6">
          {PRODUCTS.map((p, i) => (
            <Reveal key={p.id} delay={i * 0.08} className="h-full">
              <article
                data-testid={`product-card-${p.id}`}
                className="group flex flex-col h-full bg-white rounded-3xl border border-sand overflow-hidden transition-all duration-500 hover:-translate-y-1.5 hover:shadow-[0_24px_60px_-24px_rgba(15,56,44,0.35)]"
              >
                <div className="relative h-60 overflow-hidden">
                  <img
                    src={p.image}
                    alt={p.name}
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <span className="absolute top-4 left-4 rounded-full bg-cream/90 backdrop-blur px-3.5 py-1.5 font-mono text-[9px] uppercase tracking-[0.22em] text-pine">
                    {p.badge}
                  </span>
                </div>
                <div className="flex flex-col grow p-6 md:p-7">
                  <p className="font-mono text-[10px] tracking-[0.3em] text-gold">
                    {String(i + 1).padStart(2, "0")}
                  </p>
                  <h3 className="mt-3 font-serif text-2xl md:text-[1.7rem] text-pine">{p.name}</h3>
                  <p className="mt-1 font-gujarati text-sm text-sage">{p.gujarati}</p>
                  <p className="mt-4 text-sm leading-relaxed text-sage grow">{p.description}</p>
                  <button
                    data-testid={`product-enquire-${p.id}`}
                    onClick={() => enquire(p.name)}
                    className="mt-6 inline-flex items-center gap-2 self-start font-mono text-[11px] uppercase tracking-[0.22em] text-brand hover:text-royal transition-colors duration-300"
                  >
                    Enquire
                    <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </button>
                </div>
              </article>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.1}>
          <p className="mt-10 text-sm text-sage max-w-2xl">
            Milk-based sweets are crafted and packaged at our local facilities — keeping production
            inside the community, creating employment and supporting the local workforce.
          </p>
        </Reveal>
      </div>
    </section>
  );
}
